import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { isObject } from 'lodash-es';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';

import { AttachmentCreateFormComponent, ConfirmModalComponent } from '@shared/components';
import { MESSAGE } from '@shared/constants';
import { AttachmentCreateModel, Note } from '@shared/models';
import { NoteService } from '@shared/services';
import { fromAuth } from '@shared/store';
import { ErrorUtils } from '@shared/utils';
import { emptySpacesValidator } from '@shared/validators';

import { AccessControlPipe } from '../../../pipes/access-control.pipe';
import { DateFormatPipe } from '../../../pipes/date-format.pipe';
import { TeamMemberNamePipe } from '../../../pipes/team-member-name.pipe';
import { NoteItemAttachmentsComponent } from '../note-item-attachments/note-item-attachments.component';

@Component({
  selector: 'app-note-item',
  templateUrl: './note-item.component.html',
  styleUrls: ['./note-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NoteItemAttachmentsComponent,
    AsyncPipe,
    DateFormatPipe,
    TeamMemberNamePipe,
    AccessControlPipe,
    RktIconComponent,
  ],
})
export class NoteItemComponent implements OnInit, AfterViewChecked, OnChanges {
  @ViewChild('noteField') noteField!: ElementRef;

  @Input() singleNote = false;

  @Input() note!: Note;

  @Input() notesLoading!: boolean;

  @Input() showPlaceholder?: boolean;

  @Output() changed = new EventEmitter<void>();

  error?: string;

  displayError = false;

  editNoteValue = new UntypedFormControl('', [emptySpacesValidator]);

  loading = false;

  isEdit = false;

  activeUserId$ = this.store.select(fromAuth.selectActiveUserId);

  attachmentsData: Partial<AttachmentCreateModel>[] = [];

  get isEditSaveDisabled(): boolean {
    return !this.editNoteValue.value || this.editNoteValue.value === this.note.contentText || this.editNoteValue.errors !== null;
  }

  get isShowSpinner() {
    return this.notesLoading || this.loading;
  }

  constructor(
    public ref: ChangeDetectorRef,
    private noteService: NoteService,
    private store: Store,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.editNoteValue.valueChanges.subscribe({
      next: () => {
        if (this.displayError) {
          this.displayError = false;
        }
      },
    });
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    const notesLoading = changes.notesLoading?.currentValue;
    this.notesLoading = notesLoading;

    if (!notesLoading) {
      this.setLoading(false);
      this.isEdit = false;
      this.error = '';
    }
  }

  onNoteEdit(): void {
    this.isEdit = true;
    this.editNoteValue.setValue(this.note.contentText);
    // Angular 21 TestBed teardown can destroy the component before this timer fires,
    // clearing the ViewChild reference; guard so the stray callback doesn't throw.
    setTimeout(() => this.noteField?.nativeElement.focus(), 100);
  }

  onNoteEditCancel(): void {
    this.isEdit = false;
    this.error = '';
    this.attachmentsData = [];
  }

  onNoteDelete(): void {
    const suspendConfirmModalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'lg',
      className: `confirm-modal state-update-confirm-modal ${this.singleNote ? '' : 'note-delete-confirm'}`,
    });
    suspendConfirmModalRef.componentInstance.customText = 'Are you sure you want to delete this note? This cannot be undone.';
    suspendConfirmModalRef.componentInstance.title = 'Delete note';

    suspendConfirmModalRef.result.then(
      (result) => {
        if (result) {
          this.onNoteDeleteConfirm();
        }
      },
      () => false,
    );
  }

  onNoteEditSave(): void {
    this.setLoading(true);

    this.noteService
      .updateNote(this.note.id, {
        contentText: this.editNoteValue.value,
        attachments: this.attachmentsData.length ? this.attachmentsData : undefined,
      })
      .subscribe({
        next: () => {
          this.changed.emit();
        },
        error: (error) => {
          this.displayError = true;
          this.handleError(error, 'updateNote');
        },
      });
  }

  onNoteDeleteConfirm(): void {
    this.setLoading(true);

    this.noteService.deleteNote(this.note.id).subscribe({
      next: () => {
        this.changed.emit();
      },
      error: (error) => {
        this.handleError(error, 'deleteNote');
      },
    });
  }

  setLoading(loading: boolean): void {
    this.loading = loading;
  }

  onNoteFieldBlur(): void {
    this.editNoteValue.setValue(this.editNoteValue.value.trim());
  }

  handleError(error: number | string | HttpErrorResponse, serviceMethod: string): void {
    this.setLoading(false);
    this.error = (isObject(error) && error.status === 403) || error === 403 ? MESSAGE.PERMISSION_DENIED : MESSAGE.GENERIC_ERROR;
    ErrorUtils.catchError(`noteService.${serviceMethod}`, this.error);
  }

  onAddAttachment(attachment: Partial<AttachmentCreateModel>): void {
    this.attachmentsData.push(attachment);
  }

  onDeleteFileBtnClick(i: number): void {
    this.attachmentsData.splice(i, 1);
  }

  onEditFileBtnClick(i: number): void {
    const attachmentEditFormModalRef = this.modalService.open(AttachmentCreateFormComponent, {
      className: 'entity-form-modal',
    });
    attachmentEditFormModalRef.componentInstance.isEditMode = true;
    attachmentEditFormModalRef.componentInstance.formModel = {
      name: this.attachmentsData[i].name,
      description: this.attachmentsData[i].description,
    };

    attachmentEditFormModalRef.result.then(
      (result) => {
        if (result) {
          this.attachmentsData[i].description = result.description;
          this.attachmentsData[i].name = result.name;
        }
      },
      () => false,
    );
  }

  onAttachmentsDropped(attachments: Partial<AttachmentCreateModel>[]): void {
    this.attachmentsData.push(...attachments);
  }
}
